<script setup lang="ts">
  import { computed, getCurrentInstance, inject, Ref, watch } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import { getClubCurrencyDefaults } from '@/domain/club/club-currency-defaults';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { IClubProgramDraft } from './club-program-draft';

  const draft = inject<Ref<IClubProgramDraft>>('clubProgramDraft')!;
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  const symbol = computed(() => restaurantStore.currency?.symbol ?? '$');
  const defaults = getClubCurrencyDefaults(
    restaurantStore.currency?.value ?? null
  );

  function toNumber(value: string): number | null {
    if (value === '') return null;
    const num = Number(value);
    return isNaN(num) ? null : num;
  }

  function addPlan() {
    draft.value.topUpPlans.push({ amount: null, bonus: null });
  }

  function removePlan(index: number) {
    draft.value.topUpPlans.splice(index, 1);
  }

  // First activation convenience: prefill a starter plan and the terms
  // template so the owner edits instead of writing from scratch.
  watch(
    () => draft.value.balanceEnabled,
    (enabled) => {
      if (!enabled) return;
      if (draft.value.topUpPlans.length === 0) {
        draft.value.topUpPlans.push({
          amount: defaults.topUpAmount,
          bonus: defaults.topUpBonus,
        });
      }
      if (!draft.value.termsText.trim()) {
        draft.value.termsText = String(
          proxy.$t('club.wizard.termsTemplate')
        );
      }
    }
  );
</script>

<template>
  <div class="club-step-balance">
    <label class="balance-toggle">
      <input
        v-model="draft.balanceEnabled"
        class="balance-toggle-checkbox"
        type="checkbox"
      />
      <span class="balance-toggle-text">
        <span class="balance-toggle-title">
          {{ $t('club.wizard.balanceToggle') }}
        </span>
        <span class="balance-toggle-hint">
          {{ $t('club.wizard.balanceToggleHint') }}
        </span>
      </span>
    </label>

    <template v-if="draft.balanceEnabled">
      <div class="plans-section">
        <label class="step-label">{{ $t('club.wizard.plansLabel') }}</label>
        <span class="step-hint">{{ $t('club.wizard.plansHint') }}</span>

        <div
          v-for="(plan, index) in draft.topUpPlans"
          :key="index"
          class="plan-row"
        >
          <div class="plan-field">
            <span class="plan-field-label">
              {{ $t('club.wizard.planAmount') }}
            </span>
            <PosUiInput
              :value="plan.amount"
              type="number"
              inputmode="decimal"
              size="sm"
              :prefix="symbol"
              :placeholder="String(defaults.topUpAmount)"
              @input="plan.amount = toNumber($event)"
            />
          </div>
          <div class="plan-field">
            <span class="plan-field-label">
              {{ $t('club.wizard.planBonus') }}
            </span>
            <PosUiInput
              :value="plan.bonus"
              type="number"
              inputmode="decimal"
              size="sm"
              :prefix="symbol"
              :placeholder="String(defaults.topUpBonus)"
              @input="plan.bonus = toNumber($event)"
            />
          </div>
          <button
            class="plan-remove"
            type="button"
            :title="$t('club.wizard.planRemove')"
            @click="removePlan(index)"
          >
            <PosUiIcon name="delete-outline" :size="20" />
          </button>
        </div>

        <PosUiButton
          color="neutral"
          variant="outline"
          size="sm"
          icon="plus"
          @click="addPlan"
        >
          {{ $t('club.wizard.planAdd') }}
        </PosUiButton>
      </div>

      <div class="limits-grid">
        <div class="step-field">
          <label class="step-label">{{ $t('club.wizard.minTopUp') }}</label>
          <PosUiInput
            :value="draft.minTopUp"
            type="number"
            inputmode="decimal"
            :prefix="symbol"
            placeholder="0"
            @input="draft.minTopUp = toNumber($event)"
          />
        </div>
        <div class="step-field">
          <label class="step-label">
            {{ $t('club.wizard.maxMemberBalance') }}
          </label>
          <PosUiInput
            :value="draft.maxMemberBalance"
            type="number"
            inputmode="decimal"
            :prefix="symbol"
            :placeholder="$t('club.wizard.noLimit')"
            @input="draft.maxMemberBalance = toNumber($event)"
          />
        </div>
      </div>

      <div class="step-field">
        <label class="step-label">{{ $t('club.wizard.termsLabel') }}</label>
        <PosUiTextarea
          :value="draft.termsText"
          :rows="5"
          :maxlength="5000"
          @input="draft.termsText = $event"
        />
        <span class="step-hint">{{ $t('club.wizard.termsHint') }}</span>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .club-step-balance {
    @apply tw-flex tw-flex-col tw-gap-4;

    .balance-toggle {
      @apply tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-border-neutral-200 tw-p-3;

      .balance-toggle-checkbox {
        @apply tw-mt-1 tw-accent-orange-600;
        width: 18px;
        height: 18px;
      }

      .balance-toggle-text {
        @apply tw-flex tw-flex-col;

        .balance-toggle-title {
          @apply tw-text-sm tw-font-semibold tw-text-neutral-900;
        }

        .balance-toggle-hint {
          @apply tw-text-xs tw-text-neutral-500;
        }
      }
    }

    .step-label {
      @apply tw-text-sm tw-font-medium tw-text-neutral-700;
    }

    .step-hint {
      @apply tw-text-xs tw-text-neutral-400;
    }

    .step-field {
      @apply tw-flex tw-flex-col tw-gap-1.5;
    }

    .plans-section {
      @apply tw-flex tw-flex-col tw-items-start tw-gap-2;

      .plan-row {
        @apply tw-flex tw-w-full tw-items-end tw-gap-2 tw-rounded-lg tw-bg-neutral-50 tw-p-2;

        .plan-field {
          @apply tw-flex tw-flex-1 tw-flex-col tw-gap-1;

          .plan-field-label {
            @apply tw-text-xs tw-font-medium tw-text-neutral-500;
          }
        }

        .plan-remove {
          @apply tw-flex tw-h-9 tw-w-9 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-text-neutral-400 tw-transition-colors hover:tw-bg-red-50 hover:tw-text-red-600;
        }
      }
    }

    .limits-grid {
      @apply tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2;
    }
  }
</style>
